import type { Directive, DirectiveBinding } from "vue";

interface LongPressBindingValue {
	handler: (e: Event) => void;
	duration?: number;
}

/**
 * 与 Android ViewConfiguration.LONG_PRESS_TIMEOUT(500ms)、iOS UILongPressGestureRecognizer(0.5s) 对齐，
 * 并且必须略早于系统阈值：否则系统的 contextmenu、选字手柄、WebView 原生长按会先弹出来，
 * 自定义菜单看起来就像没生效。
 */
const DEFAULT_DURATION = 450;

/** 手指抖动容差(px)。聊天列表里"按住气泡往上滑翻历史"非常常见，超过这个位移就判定为滚动而非长按。 */
const MOVE_TOLERANCE = 10;

/**
 * 长按成功后，系统仍会在 touchend 时合成一次 click，紧随其后还可能有 contextmenu。
 * 这段窗口用来吃掉它们，否则长按弹出的浮层（van-popover 的 overlay）会立刻收到点击并把自己关掉。
 */
const SUPPRESS_WINDOW = 400;

interface LongPressState {
	/** 把最新一次渲染的指令值同步进闭包，见 updated 钩子。 */
	setBinding: (next: DirectiveBinding<LongPressBindingValue>) => void;
	dispose: () => void;
}

const bind = (el: HTMLElement, binding: DirectiveBinding<LongPressBindingValue>): LongPressState => {
	const state = {
		binding,
		timer: null as ReturnType<typeof setTimeout> | null,
		startX: 0,
		startY: 0,
		/** 本轮手势是否已经触发过长按，用于在 touchend 同步阻止合成 click。 */
		fired: false,
		/** 抑制合成事件的截止时间戳。 */
		suppressUntil: 0,
		/** 触摸路径已接管，忽略随后合成的 mousedown，避免二次计时。 */
		touching: false,
	};

	const clear = () => {
		if (state.timer) {
			clearTimeout(state.timer);
			state.timer = null;
		}
	};

	const schedule = (e: Event, x: number, y: number) => {
		clear();
		state.fired = false;
		state.startX = x;
		state.startY = y;
		const { duration = DEFAULT_DURATION } = state.binding.value;
		state.timer = setTimeout(() => {
			state.timer = null;
			state.fired = true;
			state.suppressUntil = Date.now() + SUPPRESS_WINDOW;
			state.binding.value.handler(e);
		}, duration);
	};

	const movedTooFar = (x: number, y: number) => Math.abs(x - state.startX) > MOVE_TOLERANCE || Math.abs(y - state.startY) > MOVE_TOLERANCE;

	const onTouchStart = (e: TouchEvent) => {
		// 双指缩放之类的多指手势不算长按。
		if (e.touches.length > 1) {
			clear();
			return;
		}
		state.touching = true;
		const touch = e.touches[0];
		schedule(e, touch.clientX, touch.clientY);
	};

	const onTouchMove = (e: TouchEvent) => {
		if (!state.timer) return;
		const touch = e.touches[0];
		if (touch && movedTooFar(touch.clientX, touch.clientY)) clear();
	};

	const onTouchEnd = (e: TouchEvent) => {
		clear();
		// 必须在 touchend 里同步 preventDefault：这是唯一能阻止本轮合成 click 的时机，
		// 放到 setTimeout 里就来不及了。
		if (state.fired) {
			e.preventDefault();
			state.fired = false;
		}
		// 合成鼠标事件紧随 touchend，延迟解除接管标记，挡掉 mousedown 重新计时。
		setTimeout(() => {
			state.touching = false;
		}, SUPPRESS_WINDOW);
	};

	const onTouchCancel = () => {
		// Android 页面开始滚动时会派发 touchcancel 并停止发送 touchmove。
		clear();
		state.fired = false;
		state.touching = false;
	};

	const onMouseDown = (e: MouseEvent) => {
		if (state.touching || e.button !== 0) return;
		schedule(e, e.clientX, e.clientY);
	};

	const onMouseMove = (e: MouseEvent) => {
		if (!state.timer) return;
		if (movedTooFar(e.clientX, e.clientY)) clear();
	};

	const onMouseEnd = () => {
		clear();
		state.fired = false;
	};

	/** 长按目标上永远不需要系统右键/长按菜单，直接屏蔽，避免和自定义菜单打架。 */
	const onContextMenu = (e: Event) => e.preventDefault();

	// 捕获阶段拦截：既能挡住子元素上的 @click（如气泡内的链接跳转），
	// 也能挡住冒泡到 van-popover__wrapper 的点击。
	const onClickCapture = (e: MouseEvent) => {
		if (Date.now() < state.suppressUntil) {
			e.stopPropagation();
			e.preventDefault();
		}
	};

	el.addEventListener("touchstart", onTouchStart, { passive: true });
	el.addEventListener("touchmove", onTouchMove, { passive: true });
	el.addEventListener("touchend", onTouchEnd, { passive: false });
	el.addEventListener("touchcancel", onTouchCancel);
	el.addEventListener("mousedown", onMouseDown);
	el.addEventListener("mousemove", onMouseMove);
	el.addEventListener("mouseup", onMouseEnd);
	el.addEventListener("mouseleave", onMouseEnd);
	el.addEventListener("contextmenu", onContextMenu);
	el.addEventListener("click", onClickCapture, true);

	const dispose = () => {
		clear();
		el.removeEventListener("touchstart", onTouchStart);
		el.removeEventListener("touchmove", onTouchMove);
		el.removeEventListener("touchend", onTouchEnd);
		el.removeEventListener("touchcancel", onTouchCancel);
		el.removeEventListener("mousedown", onMouseDown);
		el.removeEventListener("mousemove", onMouseMove);
		el.removeEventListener("mouseup", onMouseEnd);
		el.removeEventListener("mouseleave", onMouseEnd);
		el.removeEventListener("contextmenu", onContextMenu);
		el.removeEventListener("click", onClickCapture, true);
	};

	return {
		setBinding: (next) => {
			state.binding = next;
		},
		dispose,
	};
};

const LongPressDirective: Directive<HTMLElement, LongPressBindingValue> = {
	mounted(el, binding) {
		el._longPress = bind(el, binding);
	},

	updated(el, binding) {
		// binding.value.handler 每次渲染都是新建的闭包（携带当前 item）；
		// v-for 复用 DOM 时必须同步过来，否则长按会操作到上一条消息。
		el._longPress?.setBinding(binding);
	},

	unmounted(el) {
		el._longPress?.dispose();
		delete el._longPress;
	},
};

// 类型扩展
declare global {
	interface HTMLElement {
		_longPress?: LongPressState;
	}
}

export default LongPressDirective;
